import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import humanTime from "flarum/common/helpers/humanTime";
import { MessageStreamSkeleton } from "./Skeletons";
import { messagePreview } from "../../common/utils/preview";
import { authorAvatar, authorName } from "../utils/bot";
import iconLabel from "../utils/iconLabel";
/**
 * The messages the actor has bookmarked, across every channel.
 *
 * The bookmark button has been on every message row from the start, and the filter
 * behind this has been in the API just as long — but nothing ever listed them, so
 * bookmarking was a one-way action: you could save a message and then had no way to
 * find it again. This is the other half.
 *
 * Rows are summaries rather than full ChatMessage components on purpose. A message
 * row draws reply, edit and pin actions from its own capability flags, and those
 * only make sense next to the conversation they belong to; here the useful action
 * is "take me to it", so each row is a link to the message in its channel.
 */
export default class BookmarksList extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "messages", []);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "working", null);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  view() {
    if (this.loading) {
      return m("div", {
        className: "ChatBookmarks"
      }, MessageStreamSkeleton(4));
    }
    if (this.messages.length === 0) {
      return m("div", {
        className: "ChatBookmarks"
      }, m("div", {
        className: "ChatBrowse-empty"
      }, app.translator.trans("ramon-chat.forum.bookmarks.empty")));
    }
    return m("div", {
      className: "ChatBookmarks"
    }, this.messages.map(message => this.row(message)));
  }
  row(message) {
    const channel = this.attrs.state.channel(message.channelId());
    const at = message.createdAt();
    const id = Number(message.id());
    return m("div", {
      className: "ChatBookmarks-row",
      key: message.id()
    }, authorAvatar(message), m("button", {
      type: "button",
      className: "ChatBookmarks-body",
      onclick: () => this.open(message)
    }, m("div", {
      className: "ChatBookmarks-meta"
    }, m("span", {
      className: "ChatBookmarks-author"
    }, authorName(message)), channel ? m("span", {
      className: "ChatBookmarks-channel"
    }, channel.displayName()) : null, at ? m("span", null, humanTime(at)) : null), m("div", {
      className: "ChatBookmarks-excerpt"
    }, this.excerpt(message))), m(Button, Object.assign({
      className: "Button Button--icon Button--flat ChatBookmarks-remove",
      icon: "fas fa-bookmark",
      loading: this.working === id
    }, iconLabel(app.translator.trans("ramon-chat.forum.bookmarks.remove", {}, true)), {
      onclick: () => this.remove(message)
    })));
  }
  excerpt(message) {
    // Shared with the pinned bar and the other one-line previews, so a bookmarked
    // announcement reads as its title rather than as `**[title](/d/1)**`.
    const text = messagePreview(message, 160);
    if (text === "") {
      return app.translator.trans("ramon-chat.forum.bookmarks.no_text", {}, true);
    }
    return text;
  }
  async load() {
    try {
      const results = await app.store.find("chat-messages", {
        filter: {
          bookmarked: true
        },
        sort: "-createdAt",
        page: {
          limit: 50
        }
      });
      this.messages = Array.isArray(results) ? results : [];
    } catch (_unused) {
      this.messages = [];
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  /**
   * Opens the channel the message lives in, and its thread when it has one — a
   * bookmarked thread reply is not in the channel window, so routing to the channel
   * alone would land somewhere the message is not.
   */
  open(message) {
    const channelId = message.channelId();
    if (!channelId) return;
    const threadId = message.threadId();
    this.attrs.state.setActiveChannel(channelId);
    this.attrs.state.activeThreadId = threadId != null ? threadId : null;
    m.route.set(threadId ? app.route("chat.thread", {
      id: channelId,
      threadId
    }) : app.route("chat.channel", {
      id: channelId
    }));
  }
  async remove(message) {
    const id = Number(message.id());
    this.working = id;
    m.redraw();
    try {
      await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat-messages/").concat(id, "/bookmark"),
        body: {
          data: {
            attributes: {}
          }
        }
      });

      // Dropped from the list rather than re-fetched: the endpoint toggles, so a
      // successful call means it is no longer bookmarked.
      this.messages = this.messages.filter(m => Number(m.id()) !== id);
      message.pushAttributes({
        isBookmarked: false
      });
    } catch (_unused2) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.bookmarks.remove_failed"));
    } finally {
      this.working = null;
      m.redraw();
    }
  }
}
flarum.reg.add('ramon-chat', 'forum/components/BookmarksList', BookmarksList);